import { baseApi } from '@/api/core/baseApi';
import type { Schema } from '@voley/shared-types';
import type {
  AvailablePhoneNumber,
  SearchAvailablePhoneNumbersParams,
  PurchasePhoneNumberRequest,
  PurchasedPhoneNumberResponse,
  OrganizationPhoneNumber,
} from '../types';

export const twilioPhoneNumbersApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // Get organization's current phone number (from integrations - for messaging)
    getOrganizationPhoneNumber: build.query<OrganizationPhoneNumber, void>({
      query: () => ({
        url: '/integrations/twilio/phone-number',
        method: 'GET',
      }),
      transformResponse: (resp: Schema<'TwilioGetPhoneNumberResponse'>) => resp.data,
      providesTags: ['TwilioPhoneNumber'],
    }),

    // Search for available phone numbers by area code
    // Uses /api/phone-registrations/available-numbers endpoint
    searchAvailablePhoneNumbers: build.query<AvailablePhoneNumber[], SearchAvailablePhoneNumbersParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.countryCode) searchParams.append('countryCode', params.countryCode);
        if (params.areaCode) searchParams.append('areaCode', params.areaCode);
        if (params.contains) searchParams.append('contains', params.contains);
        if (params.limit) searchParams.append('limit', params.limit.toString());
        
        return {
          url: `/phone-registrations/available-numbers?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (resp: Schema<'TwilioRegistrationAvailableNumbersResponse'>) => resp.data,
    }),

    // Purchase a NEW phone number and create a registration record
    // Uses /api/phone-registrations/purchase endpoint
    // This does NOT create an Integration - only a NumberRegistration record
    // Multiple numbers can be purchased per organization
    purchaseNewNumber: build.mutation<PurchasedPhoneNumberResponse, PurchasePhoneNumberRequest>({
      query: (body) => ({
        url: '/phone-registrations/purchase',
        method: 'POST',
        body,
      }),
      transformResponse: (resp: Schema<'TwilioRegistrationPurchaseResponse'>) => resp.data,
      // Invalidate the number registration list so it refreshes after purchase
      invalidatesTags: [{ type: 'NumberRegistration', id: 'LIST' }],
    }),

    // Release current phone number (from integrations - for messaging phone number)
    releasePhoneNumber: build.mutation<{ message: string }, void>({
      query: () => ({
        url: '/integrations/twilio/phone-numbers/current',
        method: 'DELETE',
      }),
      transformResponse: (resp: { success: true; data: { message: string } }) => resp.data,
      invalidatesTags: ['TwilioPhoneNumber'],
    }),
  }),
});

export const {
  useGetOrganizationPhoneNumberQuery,
  useLazySearchAvailablePhoneNumbersQuery,
  useSearchAvailablePhoneNumbersQuery,
  usePurchaseNewNumberMutation,
  useReleasePhoneNumberMutation,
} = twilioPhoneNumbersApi;
